import { useThemePref, type ThemePref } from '../lib/theme.js';

/**
 * One discrete button in the corner rather than a three-wide control: it shows
 * the state you're in and cycles System → Light → Dark → System. The label
 * says both halves ("Theme: System — click for Light") because an icon alone
 * can't tell you what the next press does.
 */
const ORDER: ThemePref[] = ['system', 'light', 'dark'];
const NAMES: Record<ThemePref, string> = { system: 'System', light: 'Light', dark: 'Dark' };

function Icon({ pref }: { pref: ThemePref }) {
  const common = { width: 15, height: 15, viewBox: '0 0 24 24', 'aria-hidden': true } as const;
  if (pref === 'light') {
    return (
      <svg {...common} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="4.2" />
        <path d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.4 5.4l1.6 1.6M17 17l1.6 1.6M18.6 5.4L17 7M7 17l-1.6 1.6" />
      </svg>
    );
  }
  if (pref === 'dark') {
    return (
      <svg {...common} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
        <path d="M20 14.2A8.4 8.4 0 0 1 9.8 4a8.4 8.4 0 1 0 10.2 10.2z" />
      </svg>
    );
  }
  // System: a circle half-filled — the same glyph convention as "contrast".
  return (
    <svg {...common} fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 3.4a8.6 8.6 0 0 1 0 17.2z" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function ThemeButton() {
  const [pref, setPref] = useThemePref();
  const next = ORDER[(ORDER.indexOf(pref) + 1) % ORDER.length]!;
  return (
    <button
      className="ghost small icon-btn"
      onClick={() => setPref(next)}
      title={`Theme: ${NAMES[pref]} — click for ${NAMES[next]}`}
      aria-label={`Theme: ${NAMES[pref]}. Click for ${NAMES[next]}.`}
    >
      <Icon pref={pref} />
    </button>
  );
}
